/**
 * Asset Adapter
 * Converts web-app resource data to the new Asset type for integration-components
 */

import type { Asset } from '../types';

/**
 * Web-app resource structure (from appstore.js)
 */
export interface WebAppResource {
  _id: string;
  name: string;
  thumbnail: string;
  file?: {
    name?: string;
    size?: number;
    type?: string;
  };
  width?: number;
  height?: number;
  type?: string;
  fancyFileType?: string;
  isPublic?: boolean;
  allowedActions?: string[];
  versionNumber?: number;
  description?: string;
}

/**
 * Determines if a resource is restricted based on public status and allowed actions
 */
function isResourceRestricted(
  isPublic: boolean | undefined,
  allowedActions: string[] | undefined
): boolean {
  if (!allowedActions || allowedActions.length === 0) {
    return true;
  }
  if (allowedActions.length === 1 && allowedActions.includes('list')) {
    return true;
  }
  return isPublic === false;
}

/**
 * Extracts file type from filename or type fields
 */
function getFileType(resource: WebAppResource): string {
  // Try to get from file.type first
  if (resource.file?.type) {
    return resource.file.type;
  }
  
  // Try to get from type field
  if (resource.type) {
    return resource.type;
  }
  
  // Try to get from fancyFileType
  if (resource.fancyFileType) {
    return resource.fancyFileType.toLowerCase();
  }
  
  // Try to extract from filename
  if (resource.file?.name) {
    const parts = resource.file.name.split('.');
    if (parts.length > 1) {
      const extension = parts[parts.length - 1];
      if (extension) {
        return extension.toLowerCase();
      }
    }
  }
  
  // Default to unknown
  return 'unknown';
}

/**
 * Gets MIME type from file or constructs from extension
 */
function getMimeType(resource: WebAppResource): string | undefined {
  // If file.type looks like a MIME type, use it
  if (resource.file?.type && resource.file.type.includes('/')) {
    return resource.file.type;
  }
  
  // Otherwise, we don't have a reliable MIME type
  return undefined;
}

/**
 * Convert a single web-app resource object to the new Asset interface
 * 
 * @param resource - Web-app resource object
 * @returns Asset object compatible with new components
 * 
 * @example
 * ```typescript
 * const resource = {
 *   _id: 'abc123',
 *   name: 'My Image',
 *   thumbnail: 'https://...',
 *   file: { name: 'image.jpg', size: 1024000 },
 *   type: 'jpg',
 *   isPublic: true,
 *   allowedActions: ['view', 'download']
 * };
 * 
 * const asset = adaptResource(resource);
 * // Returns: { id: 'abc123', name: 'My Image', fileType: 'jpg', ... }
 * ```
 */
export function adaptResource(resource: WebAppResource): Asset {
  const isRestricted = isResourceRestricted(resource.isPublic, resource.allowedActions);
  
  return {
    id: resource._id,
    name: resource.name,
    thumbnail: resource.thumbnail || '',
    fileType: getFileType(resource),
    fileSize: resource.file?.size ?? 0,
    width: resource.width,
    height: resource.height,
    mimeType: getMimeType(resource),
    filename: resource.file?.name,
    description: resource.description,
    isPublic: resource.isPublic ?? !isRestricted,
    allowedActions: resource.allowedActions ?? [],
    versionNumber: resource.versionNumber,
  };
}

/**
 * Convert an array of web-app resource objects to Asset array
 * 
 * @param resources - Array of web-app resource objects
 * @returns Array of Asset objects compatible with new components
 * 
 * @example
 * ```typescript
 * const resources = appStore.gridData.resource[0] || [];
 * const assets = adaptResources(resources);
 * ```
 */
export function adaptResources(resources: WebAppResource[]): Asset[] {
  if (!Array.isArray(resources)) {
    return [];
  }
  return resources.map(adaptResource);
}

/**
 * Type guard to check if an object is a valid WebAppResource
 */
export function isWebAppResource(obj: unknown): obj is WebAppResource {
  if (typeof obj !== 'object' || obj === null) {
    return false;
  }
  
  const resource = obj as Record<string, unknown>;
  return typeof resource._id === 'string' && typeof resource.name === 'string';
}